import { useMutation, UseMutationResult, useQueryClient } from 'react-query';
import { EditedCustomer } from '@/components/blocks/simple-customer/simple-customer';
import { QueryId } from '@/integrations/content/utils';
import getCulture from '@/libraries/getters/get-culture';
import getLocale from '@/libraries/getters/get-locale';

type UseMutationUpdatePreBookingCustomerData =
  Booking.Responses.PatchCustomerResponse;

interface UseMutationUpdatePreBookingCustomerError {
  errors: any;
  message?: string;
}

interface UseMutationUpdatePreBookingCustomerParameters {
  brand?: Content.Properties.Brand;
  customer: Omit<EditedCustomer, 'originalFields'>;
  invalidationKeys?: QueryId[];
  parameters: Content.Components['IBookingHeadlessParameters'] & {
    id: string;
    pageId: string;
  };
}

const useMutationUpdatePreBookingCustomer = () => {
  const locale = getLocale();
  const queryClient = useQueryClient();

  const mutation: UseMutationResult<
    UseMutationUpdatePreBookingCustomerData,
    UseMutationUpdatePreBookingCustomerError,
    UseMutationUpdatePreBookingCustomerParameters
  > = useMutation(
    async parameters => {
      const fetchResponse = await fetch(
        '/api/booking/pre-booking/customers/update-customer',
        {
          body: JSON.stringify({
            bookingStateId: parameters.parameters.bookingStateId,
            brand: parameters.brand,
            customer: parameters.customer,
          }),
          headers: {
            ['Accept-Language']: getCulture(locale),
            ['Content-Type']: 'application/json',
          },
          method: 'POST',
        },
      )
        .then(response => {
          if (response.ok) return response.json();

          throw new Error(`${response.status} - ${response.statusText}`);
        })
        .catch(error => {
          console.error(
            'bookingFlow.useMutationUpdatePreBookingCustomer',
            error,
          );

          throw error;
        });

      return fetchResponse;
    },
    {
      onError: async error => error,
      onSuccess: (_data, parameters) => {
        parameters.invalidationKeys?.forEach(queryId => {
          queryClient.invalidateQueries([queryId]);
        });
      },
    },
  );

  return mutation;
};

export default useMutationUpdatePreBookingCustomer;
